import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { 
  Calendar, MapPin, Route, Car, Euro,
  TrendingUp, Clock, AlertCircle, CheckCircle, XCircle,
  ArrowUpRight, RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) return;

    setLoading(true);
    setError("");

    try {
      const [statsRes, bookingsRes] = await Promise.all([
        fetch(`${API_BASE}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/admin/recent-bookings?limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (!statsRes.ok || !bookingsRes.ok) {
        if (statsRes.status === 401 || bookingsRes.status === 401) {
          localStorage.removeItem("adminToken");
          localStorage.removeItem("adminInfo");
          navigate("/admin/login");
          return;
        }
        throw new Error("Failed to fetch dashboard data");
      }

      const [statsData, bookingsData] = await Promise.all([
        statsRes.json(),
        bookingsRes.json()
      ]);

      setStats(statsData);
      setRecentBookings(bookingsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-700">{error}</p>
          <Button onClick={fetchData} className="mt-4">
            Try Again
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="dashboard-home">
        {/* Welcome Section */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Welcome back!</h1>
            <p className="text-slate-500">Here's what's happening with your business today.</p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Quick Stats - Bookings */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard 
            title="Total Bookings" 
            value={stats?.bookings?.total || 0} 
            icon={<Calendar />} 
            color="blue"
            subtitle={`${stats?.bookings?.today || 0} today`}
          />
          <StatCard 
            title="Pending" 
            value={stats?.bookings?.pending || 0} 
            icon={<AlertCircle />} 
            color="yellow"
            subtitle="Needs attention"
          />
          <StatCard 
            title="Confirmed" 
            value={stats?.bookings?.confirmed || 0} 
            icon={<CheckCircle />} 
            color="green"
            subtitle="Ready to go"
          />
          <StatCard 
            title="Total Revenue" 
            value={`€${stats?.revenue?.total || 0}`} 
            icon={<Euro />} 
            color="emerald"
            subtitle={`€${stats?.revenue?.paid || 0} paid`}
          />
        </div>

        {/* Content Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <ContentCard 
            title="Locations" 
            value={stats?.content?.locations || 0}
            icon={<MapPin />}
            link="/admin/locations"
          />
          <ContentCard 
            title="Routes" 
            value={stats?.content?.routes || 0}
            icon={<Route />}
            link="/admin/routes"
          />
          <ContentCard 
            title="Vehicles" 
            value={stats?.content?.vehicles || 0}
            icon={<Car />}
            link="/admin/vehicles"
          />
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Bookings */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Recent Bookings</h3>
              <Link 
                to="/admin/bookings" 
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                View all <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="divide-y divide-slate-100">
              {recentBookings.length === 0 ? (
                <div className="px-4 py-8 text-center text-slate-500">
                  No bookings yet
                </div>
              ) : (
                recentBookings.map((booking) => (
                  <div key={booking.booking_id} className="px-4 py-3 hover:bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{booking.name}</p>
                        <p className="text-xs text-slate-500">
                          {`${booking.from_name} → ${booking.to_name}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">€{booking.total_price}</p>
                        <StatusBadge status={booking.booking_status} />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">Quick Actions</h3>
            </div>
            <div className="p-4 space-y-3">
              <QuickAction 
                title="Manage Bookings" 
                description="View, confirm, or cancel bookings"
                icon={<Calendar />}
                link="/admin/bookings"
                color="blue"
              />
              <QuickAction 
                title="Update Routes" 
                description="Edit prices, descriptions, and SEO text"
                icon={<Route />}
                link="/admin/routes"
                color="indigo"
              />
              <QuickAction 
                title="Vehicle Settings" 
                description="Update vehicle types and pricing"
                icon={<Car />}
                link="/admin/vehicles"
                color="emerald"
              />
            </div>
          </div>
        </div>

        {/* Booking Stats Breakdown */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-900 mb-4">Booking Breakdown</h3>
          <div className="grid grid-cols-4 gap-4">
            <MiniStat label="Total Transfers" value={stats?.bookings?.transfers || 0} />
            <MiniStat label="Round Trips" value={stats?.bookings?.return_trips || 0} />
            <MiniStat label="This Week" value={stats?.bookings?.this_week || 0} />
            <MiniStat label="Cancelled" value={stats?.bookings?.cancelled || 0} negative />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

// Stat Card Component
function StatCard({ title, value, icon, color, subtitle }) {
  const colors = {
    blue: "from-blue-500 to-blue-600",
    yellow: "from-yellow-400 to-yellow-500",
    green: "from-green-500 to-green-600",
    emerald: "from-emerald-500 to-emerald-600",
  };
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`stat-${title.toLowerCase().replace(' ', '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <span className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colors[color]} flex items-center justify-center text-white`}>
          {React.cloneElement(icon, { className: "w-5 h-5" })}
        </span>
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500">{title}</div>
      {subtitle && (
        <div className="text-xs text-slate-400 mt-1">{subtitle}</div>
      )}
    </div>
  );
}

// Content Card Component
function ContentCard({ title, value, icon, link }) {
  return (
    <Link 
      to={link}
      className="bg-white rounded-xl border border-slate-200 p-4 hover:border-blue-300 hover:shadow-sm transition-all group"
      data-testid={`content-${title.toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
            {React.cloneElement(icon, { className: "w-4 h-4" })}
          </span>
          <div>
            <div className="text-xl font-bold text-slate-900">{value}</div>
            <div className="text-xs text-slate-500">{title}</div>
          </div>
        </div>
        <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
      </div>
    </Link>
  );
}

// Quick Action Component
function QuickAction({ title, description, icon, link, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    indigo: "bg-indigo-50 text-indigo-600",
    purple: "bg-purple-50 text-purple-600",
    emerald: "bg-emerald-50 text-emerald-600",
  };

  return (
    <Link
      to={link}
      className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 transition-colors group"
      data-testid={`action-${title.toLowerCase().replace(' ', '-')}`}
    >
      <span className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center`}>
        {React.cloneElement(icon, { className: "w-5 h-5" })}
      </span>
      <div className="flex-1">
        <p className="font-medium text-slate-900 text-sm group-hover:text-blue-600 transition-colors">
          {title}
        </p>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
      <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
    </Link>
  );
}

// Status Badge Component
function StatusBadge({ status }) {
  const styles = {
    pending: "bg-yellow-100 text-yellow-700",
    confirmed: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700"
  };
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}

// Mini Stat Component
function MiniStat({ label, value, negative }) {
  return (
    <div className="text-center p-3 bg-slate-50 rounded-lg">
      <div className={`text-xl font-bold ${negative ? "text-red-600" : "text-slate-900"}`}>
        {value}
      </div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
